import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { TrendingUp, ShieldCheck, Leaf } from 'lucide-react';

const AnimatedCounter = ({ end, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = Date.now();
        const animate = () => {
          const p = Math.min((Date.now() - start) / 2000, 1);
          setCount(Math.round((1 - Math.pow(1 - p, 3)) * end));
          if (p < 1) requestAnimationFrame(animate);
        };
        animate();
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);

  return <span ref={ref}>{count}{suffix}</span>;
};

export const ValueProposition = () => {
  const { t, language } = useLanguage();

  const values = [
    { icon: TrendingUp, title: t('value.roi.title'), text: t('value.roi.text'), testId: 'value-roi' },
    { icon: ShieldCheck, title: t('value.legal.title'), text: t('value.legal.text'), testId: 'value-legal' },
    { icon: Leaf, title: t('value.sustainable.title'), text: t('value.sustainable.text'), testId: 'value-sustainable' }
  ];

  return (
    <section id="value" data-testid="value-section" className="bg-[#FAFAF7] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24">
        {/* Aurven-style: badge + large heading left, description right */}
        <div className="mb-20">
          <span className="inline-block font-inter text-xs uppercase tracking-[0.25em] text-[#0A2235]/60 border border-[#0A2235]/15 px-4 py-1.5 rounded-sm mb-8">
            {language === 'es' ? 'La diferencia Marea Riviera' : 'The Marea Riviera Difference'}
          </span>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <h2 className="font-playfair text-[clamp(2rem,5vw,4rem)] text-[#0A2235] leading-[1.08] tracking-tight" data-testid="value-section-title">
              {language === 'es' ? (
                <>La firma Marea Riviera de <em className="text-[#2BBFBF]">excelencia</em> refinada</>
              ) : (
                <>The Marea Riviera signature of refined <em className="text-[#2BBFBF]">excellence</em></>
              )}
            </h2>
            <p className="font-inter text-[#0A2235]/60 text-base leading-[1.8] font-light lg:pt-4">
              {language === 'es'
                ? 'Desde la arquitectura hasta la operación, cada propiedad que presentamos ha pasado por un riguroso proceso de due diligence. MIA vigente, fideicomiso en regla y desarrollador con historial comprobado. Solo trabajamos con lo mejor.'
                : 'From architecture to operations, every property we present has gone through a rigorous due diligence process. Active MIA, proper trust, and developer with a proven track record. We only work with the best.'}
            </p>
          </div>
        </div>

        {/* Stats row - Aurven style: left-aligned large numbers */}
        <div className="grid grid-cols-3 gap-8 mb-24">
          {[
            { val: 641, suf: '+', label: language === 'es' ? 'Desarrollos con MIA vigente filtrados para ti.' : 'Developments with active MIA filtered for you.' },
            { val: 95, suf: '%', label: language === 'es' ? 'De inversionistas recomiendan Marea Riviera.' : 'Of investors recommend Marea Riviera.' },
            { val: 100, suf: '%', label: language === 'es' ? 'Certeza jurídica en cada operación.' : 'Legal certainty in every transaction.' },
          ].map((s, i) => (
            <div key={i}>
              <p className="font-playfair text-[clamp(2.5rem,5vw,4.5rem)] text-[#0A2235] leading-none mb-3">
                <AnimatedCounter end={s.val} suffix={s.suf} />
              </p>
              <p className="font-inter text-[#0A2235]/50 text-sm leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Value cards - bordered like Aurven */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {values.map((v) => (
            <div key={v.testId} className="group border border-[#0A2235]/10 rounded-2xl p-8 hover:border-[#2BBFBF]/50 hover:shadow-lg transition-all duration-500" data-testid={v.testId}>
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#D4EDE8] text-[#0A2235] rounded-xl mb-6 group-hover:bg-[#2BBFBF] group-hover:text-[#FAFAF7] transition-all duration-500">
                <v.icon className="w-5 h-5" />
              </div>
              <h3 className="font-playfair text-xl text-[#0A2235] mb-3">{v.title}</h3>
              <p className="font-inter text-[#0A2235]/50 text-sm leading-[1.7] font-light">{v.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
